import React from 'react';
import { Button, Dropdown, MenuProps, Space } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';

/**
 * Standard record-level action control: a "Back" button beside an "Actions"
 * dropdown. This is the pattern every detail screen should use for Edit,
 * Delete, Print, Download and any module-specific verb.
 *
 * It was previously copy-pasted into each info page; keeping it here means a
 * new module gets the same control by importing it, and a change to the
 * control applies everywhere at once.
 *
 * Menu entries follow antd's MenuProps['items'] shape, so an entry can carry
 * an icon, a `disabled` flag (used to gate Edit once a record is approved) and
 * a `danger` flag (used for Delete):
 *
 *   const items: MenuProps['items'] = [
 *     { key: 'edit',   label: 'Edit',   icon: <EditOutlined />, disabled: isApproved },
 *     { key: 'print',  label: 'Print',  icon: <PrinterOutlined /> },
 *     { key: 'delete', label: 'Delete', icon: <DeleteOutlined />, danger: true },
 *   ];
 */
interface ActionsMenuProps {
  /** Menu entries, in the order they should appear. */
  items: MenuProps['items'];
  /** Fired with the key of the chosen entry. */
  onClick: MenuProps['onClick'];
  /** Omit to hide the Back button (for screens reached without navigation). */
  onBack?: () => void;
  /** Label on the trigger; only override when "Actions" would be ambiguous. */
  label?: string;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({
  items,
  onClick,
  onBack,
  label = 'Actions',
}) => {
  return (
    <Space>
      {onBack && <Button onClick={onBack}>Back</Button>}
      <Dropdown menu={{ items, onClick }} trigger={['click']}>
        <Button>
          {label} <EllipsisOutlined />
        </Button>
      </Dropdown>
    </Space>
  );
};

export default ActionsMenu;
